// components/AnticipationBDC.tsx
// Interruption d'un bon de caisse validé — même charte que
// ValidationBDC : picklist statut 02 -> Consulter -> champs readonly +
// paramètres d'interruption -> Interrompre (branche 'I' du SP).
import React, { useEffect, useState } from "react";
import { Button, Form, Space, Spin, Table, Tabs, Select, Upload } from "antd";
import type { TabsProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { UserOutlined, UploadOutlined, DeleteOutlined, FilePdfOutlined } from '@ant-design/icons';
import {
  Typography, Box,
} from "@mui/material";
import axios from "axios";
import { IBonDeCaisse, IParametreBDC, IDemandeInterBDC } from "../../../dataTypes/GlobalDataType";
import { Crypter } from "../../../Functions/Security/Crypter";
import Data from '../../../Data/Data.json';
import { useAuthHeader, useAuthUser } from '../../../Services/Auth/sessionAuth';
import Utilisateur from "../../../Models/Utilisateur";
import { fetchDataTable2, GlobalExportToCSV, GlobalExportToExcel, GlobalExportToJSON, GlobalExportToPDF, GlobalExportToWord } from "../../../utilitaires/globalFunction";
import formatNumber from "../../../Functions/Traitement/FormatNumber";
import SweetAlert from "sweetalert2";
import ParamsPicklist from "../../../Types/ParamsPicklist";
import InputForm from "../../InputT";
import SearchInput from "../../SearchInput";
import SearchModal from "../../SearchModal";
import ExportMenu from "../../ExportMenu";

export default function AnticipationBDC() {
  const auth = useAuthUser()
  const authHeader = useAuthHeader()
  const Perss: Utilisateur = JSON.parse(JSON.stringify(auth()))

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<IBonDeCaisse | null>(null);
  // Paramètres de sortie anticipée du type (onglet « Paramètre interruption »
  // de /profilgestion/bondecaisse)
  const [paramInter, setParamInter] = useState<IParametreBDC | null>(null);

  // Boutons (charte)
  const [enregActve, setEnregActve] = useState(true)
  const [echActive, setEchActive] = useState(true)
  const [impActive, setImpActive] = useState(true)
  const [annulActive, setAnnulActive] = useState(true)
  const [consulActive, setConsulActive] = useState(false)
  const [calcActive, setCalcActive] = useState(true)
  const [interActif, setInterActif] = useState(true)   // champs interruption éditables

  interface IEcheance {
    periode: number;
    dateImput: string;
    interet: number;
    taxe: number;
    netAPayer: number;
  }
  const [echeancier, setEcheancier] = useState<IEcheance[]>([]);

  // Documents d'interruption — même logique que Safe Care :
  // libellés requis du type (DOCREQINTERBDC) -> picklist -> upload
  // du fichier -> envoyés avec la demande (NUMDEM).
  const [docRequis, setDocRequis] = useState<any[]>([]);
  const [docLibelle, setDocLibelle] = useState<string | null>(null);
  const [pendingDocs, setPendingDocs] = useState<any[]>([]);
  const totalInteret = echeancier.reduce((acc, r) => acc + r.interet, 0);
  const totalTaxe = echeancier.reduce((acc, r) => acc + r.taxe, 0);
  const totalNet = echeancier.reduce((acc, r) => acc + r.netAPayer, 0);

  // Picklist : uniquement les bons VALIDÉS (statut 02 = interruptibles)
  let searchBdc: ParamsPicklist = { CodeAgence: Perss.codage, Table: "BONDECAISSE", Colonne1: "NUMBDC", Colonne2: "NOMCLI", Colagence: 'CODAGE', Choix: 22, colfiltre: "CDETAT", valfiltre: "02" }

  // Motif d'interruption — liste fixe LIB (RUBRIQ='MOTIFINTER') :
  // même pattern que les picklists LIB (CodeAgence = rubrique, filtré
  // via Colagence='RUBRIQ')
  let searchMotif: ParamsPicklist = { CodeAgence: 'MOTIFINTER', Table: "LIB", Colonne1: "CODLIB", Colonne2: "LIBELL", Colagence: 'RUBRIQ', Choix: 22 }

  // Libellés des documents requis — picklist sur DOCREQINTERBDC
  // filtrée par le type du bon sélectionné (comme Safe Care)
  let searchDocReq: ParamsPicklist = { CodeAgence: Perss.codage, Table: "DOCREQINTERBDC", Colonne1: "IDREQ", Colonne2: "LIBDOC", Colagence: 'CODAGE', Choix: 22, colfiltre: "CODBDC", valfiltre: selected?.TYPBDC || '___' }

  function initialiazeButton() {
    setEnregActve(true)
    setEchActive(true)
    setImpActive(true)
    setAnnulActive(true)
    setConsulActive(false)
    setCalcActive(true)
    setInterActif(true)
  }

  function handleAnulle() {
    setEcheancier([])
    setSelected(null)
    setParamInter(null)
    setDocRequis([])
    setDocLibelle(null)
    setPendingDocs([])
    form.resetFields()
    initialiazeButton()
  }

  function cunsultButton() {
    setEnregActve(false)   // Interrompre disponible
    setEchActive(false)
    setImpActive(false)
    setAnnulActive(false)
    setConsulActive(false)
    setCalcActive(false)   // Calculer disponible
    setInterActif(false)   // champs interruption éditables
  }

  // Liste des bons validés (état 02)
  const fetchData = async (): Promise<IBonDeCaisse[]> => {
    try {
      var params = { CodeAgence: Perss.codage, Table: "BONDECAISSE", Colonne1: "NUMCLI", Colonne2: "NOMCLI", Colagence: "CODAGE", Init: "", choix: 32, colfiltre: "", valfiltre: "" }
      const res = await fetchDataTable2(Data.ApiUrl.AnySearch, params, authHeader());
      const list: IBonDeCaisse[] = (res || []).filter((c: IBonDeCaisse) => c.CDETAT === "02")
      return list
    } catch (err) {
      console.error("Erreur de chargement:", err);
      return [];
    }
  };

  // Libellé du code opération du bon (OPERAT.LIBELL)
  const fetchLibOpe = async (codope?: string) => {
    if (!codope) { form.setFieldValue('LIBOPE', ''); return; }
    try {
      var params = { CodeAgence: '', Table: "OPERAT", Colonne1: "CODOPE", Colonne2: "LIBELL", Colagence: '', Init: "", choix: 32, colfiltre: "", valfiltre: "" }
      const res = await fetchDataTable2(Data.ApiUrl.AnySearch, params, authHeader());
      const row = (res || []).find((o: any) => o.CODOPE === codope);
      form.setFieldValue('LIBOPE', row?.LIBELL || '');
    } catch (err) {
      console.warn('fetchLibOpe - erreur', err);
    }
  };

  // Paramètres de sortie anticipée du type BDC (PARAMBDC — onglet
  // « Paramètre interruption » : INTERRUPTION, DUREEMININTERUPT, PREAVISINTERUPT,
  // MODEINTINTERUPT, TXPENALINTERUPT, TXTAXEPENALINTERUPT)
  const getParamInter = async (cod?: string): Promise<IParametreBDC | null> => {
    if (!cod) return null;
    try {
      var params = { CodeAgence: Perss.codage, Table: "PARAMBDC", Colonne1: "CODBDC", Colonne2: "LIBELL", Colagence: "CODAGE", Init: "", choix: 32, colfiltre: "", valfiltre: "" }
      const res = await fetchDataTable2(Data.ApiUrl.AnySearch, params, authHeader());
      const row = (res || []).find((p: any) => p.CODBDC === cod) as IParametreBDC;
      setParamInter(row || null);
      return row || null;
    } catch (err) {
      console.error("Erreur de chargement:", err);
      return null;
    }
  };

  // Disponible d'un compte (GetCompte → COMPTES, même endpoint que création)
  const fetchDispon = async (numcpt?: string, field?: string) => {
    if (!numcpt || !field) return;
    try {
      const crypted = Crypter.encrypt(String(numcpt));
      const response = await axios({
        method: 'get',
        maxBodyLength: Infinity,
        url: Data.ApiUrl.GetCompte,
        headers: {
          'CypherNumCpte': crypted,
          'Authorization': authHeader(),
          'Content-Type': 'application/json'
        }
      });
      if (response.data) {
        const res = JSON.parse(Crypter.decrypt(response.data));
        if (res.StatutCode == 200 && res.Result?.Table?.[0] != null) {
          form.setFieldValue(field, formatNumber(Number(res.Result.Table[0].DISPON) || 0));
        }
      }
    } catch (err) {
      console.warn('GetCompte - erreur', err);
    }
  };

  const fmtDate = (d: any) => d ? new Date(d).toISOString().substring(0, 10) : "";

  // Nombre de jours par fréquence
  const joursFreq = (freq: string) => {
    switch ((freq || "").toUpperCase()) {
      case "A": return 360;
      case "S": return 180;
      case "T": return 90;
      case "M": return 30;
      case "B": return 15;
      case "F": return 60;
      case "H": return 7;
      case "J": return 1;
      default: return 30;
    }
  }

  // Échéancier (aperçu — même calcul front que création)
  const calculer = (row: any) => {
    const freq = (row.FREQPROV || "").toUpperCase();
    const montant = Number(row.MONTANT) || 0;
    const tauxInteret = Number(row.TXINT) || 0;
    const tauxTaxe = Number(row.TXTAXE) || 0;
    const duree = Number(row.DUREE) || 0;

    const jours = joursFreq(freq);
    const montIntBrut = (montant * tauxInteret * duree * jours) / 36000;
    const montProvFreq = Math.round(montIntBrut / duree);
    const montInt = montProvFreq * duree;
    const montTaxeBrut = (montInt * tauxTaxe) / 100;
    const montTaxeFreq = Math.round(montTaxeBrut / duree);
    const montTaxe = montTaxeFreq * duree;

    let dateEcheance = new Date(row.DATDEBUT || Perss.datjour);
    const newEcheancier: IEcheance[] = [];
    for (let i = 1; i <= duree; i++) {
      const prochaine = new Date(dateEcheance);
      prochaine.setDate(prochaine.getDate() + jours);
      newEcheancier.push({
        periode: i,
        dateImput: dateEcheance.toLocaleDateString(),
        interet: montProvFreq,
        taxe: montTaxeFreq,
        netAPayer: montProvFreq - montTaxeFreq,
      });
      dateEcheance = prochaine;
    }

    setEcheancier(newEcheancier);
    form.setFieldsValue({
      TotInt: formatNumber(montInt),
      TotTaxe: formatNumber(montTaxe),
      TotNet: formatNumber(montInt - montTaxe),
      MTNINT: montInt,
      MTNTAXE: montTaxe,
      MTNNETAPAYER: montInt - montTaxe,
      MTNPROVFREQ: montProvFreq,
      MTNTAXEFREQ: montTaxeFreq,
    });
    setEchActive(false);
  }

  // Libellés des documents requis pour le type (DOCREQINTERBDC)
  const fetchDocRequis = async (typBdc?: string) => {
    if (!typBdc) { setDocRequis([]); return; }
    try {
      var params = { CodeAgence: Perss.codage, Table: "DOCREQINTERBDC", Colonne1: "IDREQ", Colonne2: "LIBDOC", Colagence: "CODAGE", Init: "", choix: 32, colfiltre: "", valfiltre: "" }
      const res = await fetchDataTable2(Data.ApiUrl.AnySearch, params, authHeader());
      setDocRequis((res || []).filter((d: any) => d.CODBDC === typBdc));
    } catch (err) {
      console.error("Erreur de chargement:", err);
    }
  };

  // Upload d'un fichier pour le libellé sélectionné — conservé en
  // mémoire (pendingDocs) jusqu'à la création de la demande
  const handleUploadDoc = (file: File) => {
    if (!docLibelle) {
      SweetAlert.fire({ icon: 'warning', title: 'Document', text: 'Sélectionnez d\'abord le libellé du document dans la picklist.' });
      return false;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setPendingDocs((prev) => [...prev, {
        LIBDOC: docLibelle,
        IDREQ: Number(form.getFieldValue('DOCREQ')) || null,
        NOMFICHIER: file.name,
        PIECEJOINTE: reader.result,
      }]);
    };
    reader.readAsDataURL(file);
    return false;   // empêche l'upload auto d'antd
  };

  // Upload des documents rattachés à la demande créée (NUMDEM)
  const uploadDocsInterruption = async (numDem: string, numBdc: string) => {
    for (const doc of pendingDocs) {
      try {
        const payload = { ...doc, NUMDEM: numDem, NUMBDC: numBdc, CODAGE: Perss.codage, UTISAI: Perss.coduti };
        const data = Crypter.encrypt(JSON.stringify(payload));
        await axios.request({
          method: 'post',
          maxBodyLength: Infinity,
          url: Data.ApiUrl.AddDocInterruptionBDC,
          headers: {
            'CypherDoc': data,
            'Authorization': authHeader(),
            'Content-Type': 'application/json'
          }
        });
      } catch (err) {
        console.error('Erreur upload document:', err);
      }
    }
  };

  // Aperçu des frais d'interruption (CODOPEINTERRUP -> grille tarifaire,
  // même mécanisme que les frais de souscription de la validation)
  const fetchFraisInterruption = async (typBdc?: string, montant?: any) => {
    if (!typBdc || !Number(montant)) {
      form.setFieldValue('FRAISINTERRUP', '');
      return;
    }
    try {
      const response = await axios({
        method: 'get',
        maxBodyLength: Infinity,
        url: Data.ApiUrl.ApercuFraisInterruptionBDC,
        headers: {
          'CypherTypBdc': Crypter.encrypt(JSON.stringify(typBdc)),
          'CypherAgence': Crypter.encrypt(JSON.stringify(Perss.codage)),
          'CypherMontant': Crypter.encrypt(JSON.stringify(Number(montant))),
          'Authorization': authHeader(),
          'Content-Type': 'application/json'
        }
      });
      if (response.data) {
        const res = JSON.parse(Crypter.decrypt(response.data));
        const status = res?.Result?.Table?.[0];
        const detail = res?.Result?.Table1?.[0];
        if (String(status?.Status) === '200') {
          const frais = Number(detail?.Frais) || 0;
          form.setFieldValue('FRAISINTERRUP', frais > 0 ? formatNumber(frais) : 'Aucun');
        } else {
          form.setFieldValue('FRAISINTERRUP', status?.result || 'Aucun');
        }
      }
    } catch (err) {
      console.warn('ApercuFraisInterruption - réponse inattendue', err);
      form.setFieldValue('FRAISINTERRUP', 'Indisponible');
    }
  };

  // Libellé du mode de sortie anticipée (PARAMBDC.MODEINTINTERUPT)
  const libModeInter = (mode?: string) => {
    switch ((mode || 'NON').toUpperCase()) {
      case 'ACQ': return 'Intérêts acquis conservés';
      case 'DUS': return 'Intérêts dus versés';
      default: return 'Intérêts perdus';
    }
  }

  // Calcul de l'interruption piloté par PARAMBDC (onglet « Paramètre
  // anticipation ») : pénalité = TXPENALINTERUPT x jours restants, taxe =
  // TXTAXEPENALINTERUPT % de la pénalité, intérêts selon MODEINTINTERUPT
  function calculInterruptionBDC(nbJr: number) {
    const d1 = new Date(form.getFieldValue('DATDEBUT'));
    const d2 = new Date(form.getFieldValue('DATFIN'));
    const dInt = new Date(form.getFieldValue('DATINTERUP'));
    const int = Number(form.getFieldValue('MTNNETAPAYER')) || 0;
    if (isNaN(d1.getTime()) || isNaN(d2.getTime()) || isNaN(dInt.getTime())) {
      SweetAlert.fire({ icon: 'warning', title: 'Date d\'interruption invalide', target: document.body, backdrop: true });
      return;
    }

    const joursEcoules = Math.round((dInt.getTime() - d1.getTime()) / (1000 * 3600 * 24));
    let joursRestants = Math.round((d2.getTime() - dInt.getTime()) / (1000 * 3600 * 24));
    if (nbJr != 0 && nbJr != joursRestants) { joursRestants = nbJr }
    const interetAcquis = Math.round((Number(form.getFieldValue('MONTANT')) * Number(form.getFieldValue('TXINT')) * joursEcoules) / 36000);
    const penalite = Math.round((Number(form.getFieldValue('MONTANT')) * (Number(paramInter?.TXPENALINTERUPT) || 0) * joursRestants) / 36000);
    const taxePenalite = Math.round(penalite * (Number(paramInter?.TXTAXEPENALINTERUPT) || 0) / 100);

    form.setFieldsValue({
      JOURRESTANT: joursRestants,
      JOURECOULE: joursEcoules,
      PENALITE: penalite,
      TAXPENAL: taxePenalite,
      INTACQUIS: interetAcquis,
      INTDUS: int - interetAcquis,
      MODEINTER: paramInter?.MODEINTINTERUPT || 'NON',
      PREAVISINTERUPT: paramInter?.PREAVISINTERUPT || 0,
      INTEFFET: libModeInter(paramInter?.MODEINTINTERUPT),
    })
  }

  // Colonnes antd de l'échéancier (même table que création/validation)
  const columns: ColumnsType<IEcheance> = [
    { title: 'Période', dataIndex: 'periode', key: 'periode', width: 80, align: 'center' },
    { title: 'Date imputation', dataIndex: 'dateImput', key: 'dateImput', width: 120 },
    { title: 'Intérêt', dataIndex: 'interet', key: 'interet', width: 130, align: 'right', render: (v: number) => formatNumber(v) },
    { title: 'Taxe', dataIndex: 'taxe', key: 'taxe', width: 110, align: 'right', render: (v: number) => formatNumber(v) },
    { title: 'Net à payer', dataIndex: 'netAPayer', key: 'netAPayer', width: 130, align: 'right', render: (v: number) => formatNumber(v) },
  ];

  const tabElement = (
    <Box fontStyle="inherit" className='tabElement' style={{ top: '95%', height: 'auto' }}>
      <Spin spinning={loading}>
        <Table size='small' style={{ width: 'auto' }} columns={columns} dataSource={echeancier} rowKey="periode" scroll={{ y: 160, x: 10 }} pagination={false} />
      </Spin>
      <Box display={"flex"} fontStyle="inherit" className='border-left bordure1' style={{ marginTop: 8 }}>
        <InputForm notRequired disabled width={8} label='Intérêts bruts' name='TotInt' type='text' />
        <InputForm notRequired disabled width={8} label='Taxe retenue' name='TotTaxe' type='text' />
        <InputForm notRequired disabled width={8} label='Net versé au client' name='TotNet' type='text' />
      </Box>
    </Box>
  );

  // Exports du tableau
  const handleExport = (format: any) => {
    const title = "Exportation interruption BDC " + (selected?.NUMBDC || '');
    const exportData = echeancier.length > 0 ? echeancier : (selected ? [selected] : []);
    switch (format) {
      case 'excel': GlobalExportToExcel(exportData, title, "InterruptionBDC"); break;
      case 'pdf': GlobalExportToPDF(exportData, title, "InterruptionBDC"); break;
      case 'word': GlobalExportToWord(exportData, title, "InterruptionBDC"); break;
      case 'csv': GlobalExportToCSV(exportData, title, "InterruptionBDC"); break;
      case 'json': GlobalExportToJSON(exportData, title, "InterruptionBDC"); break;
      default: break;
    }
  };

  // Remplit le formulaire depuis un bon validé
  const fillFromBdc = (row: IBonDeCaisse) => {
    const cleanRow: any = {
      ...row,
      DATDEBUT: fmtDate(row.DATDEBUT),
      DATFIN: fmtDate(row.DATFIN),
      DATINTERUP: fmtDate(Perss.datjour),
      JOURRESTANT: 0,
      JOURECOULE: 0,
      PENALITE: 0,
      TAXPENAL: 0,
      INTACQUIS: 0,
      INTDUS: 0,
      MODEINTER: '',
      PREAVISINTERUPT: 0,
      INTEFFET: '',
      FRAISINTERRUP: '',
      lib_NUMCLI: row.NOMCLI,
    }
    setSelected(row);
    form.setFieldsValue(cleanRow);
    fetchDispon(row.CPTDEPOT, 'DISPONCPTDEPOT')
    fetchDispon(row.CPTBDC, 'DISPONCPTBDC')
    fetchDispon(row.CPTINTCLI, 'DISPONCPTINT')
    fetchLibOpe(row.CODOPE)
    getParamInter(row.TYPBDC)
    fetchFraisInterruption(row.TYPBDC, row.MONTANT)
    fetchDocRequis(row.TYPBDC)
    calculer({ ...row })
  }

  // Consulter : charge le bon validé dont le numéro est saisi
  const consult = async () => {
    const numbdc = form.getFieldValue("NUMBDC")
    if (!numbdc) {
      SweetAlert.fire({ title: "Sélectionnez ou saisissez d'abord un numéro de bon de caisse validé.", icon: "warning" })
      return
    }
    setLoading(true)
    const list = await fetchData()
    setLoading(false)
    const row = list.find(c => c.NUMBDC === numbdc)
    if (!row) {
      SweetAlert.fire({ title: "Bon de caisse introuvable ou non validé", text: "Aucun bon validé (statut 02) pour le numéro : " + numbdc, icon: "warning" })
      return
    }
    fillFromBdc(row)
    cunsultButton()
  }

  useEffect(() => {
    form.setFieldValue('DATINTERUP', fmtDate(Perss.datjour))
  }, []);

  // Recalcule à la saisie (date interruption / jours restants)
  const onValuesChange = (changed: any) => {
    // Picklist document : le libellé choisi conditionne l'upload
    if (changed.lib_DOCREQ !== undefined || changed.DOCREQ !== undefined) {
      setDocLibelle(form.getFieldValue('lib_DOCREQ') || null);
    }
    if (!selected) return;
    if (changed.DATINTERUP !== undefined && !interActif) {
      calculInterruptionBDC(0);
    }
    if (changed.JOURRESTANT !== undefined && !interActif) {
      const nb = Number(changed.JOURRESTANT) || 0;
      calculInterruptionBDC(nb);
    }
  };

  // ------------------------------------------------------------------
  // Soumission — crée une DEMANDE d'interruption (état '01'), sans
  // mouvement comptable. Elle sera validée par un autre utilisateur dans
  // /bondecaisse/validation-interruption (quatre-yeux).
  // ------------------------------------------------------------------
  const onFinish = async () => {
    const values = form.getFieldsValue(true);

    if (!selected || selected.CDETAT !== '02') {
      SweetAlert.fire({
        icon: "warning",
        title: "Interruption bon de caisse",
        text: "Impossible d'interrompre ce bon de caisse : il n'est pas validé ou a déjà été traité.",
        target: document.body,
        backdrop: true,
      });
      return;
    }

    // Contrôles paramétrés du type (onglet « Paramètre interruption ») —
    // la sortie anticipée doit être autorisée et la durée min atteinte.
    if (paramInter?.INTERRUPTION !== 'O') {
      SweetAlert.fire({
        icon: "warning",
        title: "Interruption non autorisée",
        text: `La sortie anticipée n'est pas autorisée pour le type ${selected.TYPBDC}. Activez-la dans /profilgestion/bondecaisse (onglet « Paramètre interruption »).`,
        target: document.body,
        backdrop: true,
      });
      return;
    }

    const joursEcoules = Number(values.JOURECOULE) || 0;
    if ((paramInter?.DUREEMININTERUPT || 0) > 0 && joursEcoules < paramInter.DUREEMININTERUPT) {
      SweetAlert.fire({
        icon: "warning",
        title: "Durée minimale non atteinte",
        text: `La sortie anticipée exige ${paramInter.DUREEMININTERUPT} jours minimum ; le bon n'en compte que ${joursEcoules}.`,
        target: document.body,
        backdrop: true,
      });
      return;
    }

    // Documents obligatoires configurés pour le type : tout libellé
    // marqué 'O' doit avoir son fichier uploadé (même contrôle que
    // Safe Care à la demande de suspension)
    const manquants = docRequis.filter((d: any) => d.OBLIGATOIRE === 'O' && !pendingDocs.some((p) => p.IDREQ === d.IDREQ));
    if (manquants.length > 0) {
      SweetAlert.fire({
        icon: "warning",
        title: "Documents obligatoires manquants",
        text: `Uploadez les documents : ${manquants.map((m: any) => m.LIBDOC).join(', ')}.`,
        target: document.body,
        backdrop: true,
      });
      return;
    }

    // Net impact client : capital + intérêts gardés (selon mode) -
    // pénalité - taxe pénalité - frais d'interruption (grille)
    const mode = paramInter?.MODEINTINTERUPT || 'NON';
    const netTotal = Number(values.MTNNETAPAYER) || 0;
    const intGarde = mode === 'DUS' ? netTotal : mode === 'ACQ' ? (Number(values.INTACQUIS) || 0) : 0;
    const fraisInt = parseFloat(String(values.FRAISINTERRUP || '0').replace(/[^\d.-]/g, '')) || 0;
    const netClient = (Number(values.MONTANT) || 0) + intGarde - (Number(values.PENALITE) || 0) - (Number(values.TAXPENAL) || 0) - fraisInt;

    const demande: IDemandeInterBDC = {
      NUMBDC: values.NUMBDC,
      REFBDC: values.REFBDC,
      NUMCLI: values.NUMCLI,
      NOMCLI: values.lib_NUMCLI || values.NOMCLI,
      TYPBDC: values.TYPBDC,
      DATINTERUP: values.DATINTERUP,
      MOTIF: values.MOTIF,
      LIBMOTIF: values.lib_MOTIF,
      OBSERVATION: values.OBSERVATION,
      MONTANT: Number(values.MONTANT) || 0,
      JOURECOULE: joursEcoules,
      JOURRESTANT: Number(values.JOURRESTANT) || 0,
      PENALITE: Number(values.PENALITE) || 0,
      TAXPENAL: Number(values.TAXPENAL) || 0,
      FRAISINT: fraisInt,
      INTACQUIS: Number(values.INTACQUIS) || 0,
      INTDUS: Number(values.INTDUS) || 0,
      MODEINT: mode,
      NETCLIENT: netClient,
      UTISAI: Perss.coduti,
      CODAGE: Perss.codage,
    }

    const result = await SweetAlert.fire({
      title: "Demande d'interruption",
      text: `Créer une demande d'interruption pour le bon ${values.NUMBDC} ? (impact net client : ${formatNumber(netClient)})`,
      showCancelButton: true,
      confirmButtonText: "Oui",
      cancelButtonText: "Non",
      icon: "question",
      confirmButtonColor: "#116599",
      cancelButtonColor: "darkred",
      target: document.body,
    });

    if (result.isConfirmed) {
      setLoading(true);
      let data = Crypter.encrypt(JSON.stringify(demande));
      let config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: Data.ApiUrl.DemanderInterruptionBDC,
        headers: {
          'CypherDemandeInter': data,
          'Authorization': authHeader(),
          'Content-Type': 'application/json'
        },
        data: data
      };

      axios.request(config)
        .then((response) => {
          setLoading(false);
          if (response.data) {
            const res = JSON.parse(Crypter.decrypt(response.data));
            if (res.StatutCode == 200) {
              const numDem = res.Result;   // NUMDEM généré côté serveur
              const numBdc = demande.NUMBDC || '';
              // Upload des documents rattachés à la demande (Safe Care)
              if (pendingDocs.length > 0) {
                uploadDocsInterruption(numDem, numBdc).then(() => {
                  handleAnulle();
                  SweetAlert.fire({
                    title: "Demande enregistrée",
                    text: `Demande ${numDem} enregistrée avec ${pendingDocs.length} document(s). Elle sera effective après validation par un autre utilisateur.`,
                    icon: "success"
                  })
                });
              } else {
                handleAnulle();
                SweetAlert.fire({
                  title: "Demande enregistrée",
                  text: `Demande ${numDem} enregistrée. Elle sera effective après validation par un autre utilisateur.`,
                  icon: "success"
                })
              }
            }
            else {
              SweetAlert.fire({ title: "Warning", text: res.Message, icon: "warning" })
            }
          }
        })
        .catch((error) => {
          setLoading(false);
          console.log(error)
          SweetAlert.fire({ title: "Error Front", text: error, icon: "error" })
        });
    }
  };

  const onFinishFailed = async (errorInfo: any) => {
    var errors: any = [];
    errorInfo.errorFields.map((error: any) => {
      errors.push(error.name + "");
    })
    SweetAlert.fire({ title: "Veuillez remplir tous les champs obligatoires", text: errors.toString().replaceAll(',', " - "), icon: 'warning', confirmButtonText: 'OK' })
  };

  // ------------------------------------------------------------------
  // Onglet interruption (même layout que validation)
  // ------------------------------------------------------------------
  const items: TabsProps['items'] = [
    {
      key: '1',
      label: (
        <span id='Tab'>
          <UserOutlined />
          Interruption bon de caisse
        </span>
      ),
      children: (
        <div className='tabItem'>
          <div>
            <Box fontStyle="inherit" className='border' width={"85%"} position={"absolute"}>
              <Box display={"flex"} fontStyle="inherit" className='border-left'>

                <Box fontStyle="inherit" className='border-left' style={{ flex: 1 }}>
                  <SearchInput dissabledCode={!consulActive && !annulActive} notRequired notremove enable width={7} form={form} Titre='Recherche Bon de caisse' label='N° BDC' name='NUMBDC' Parametre={searchBdc} />
                  <InputForm width={11} label='Référence BDC' name='REFBDC' type='label' notRequired />
                  <InputForm width={11} label='Code Simul' name='CODSIMUL' type='label' notRequired />
                  <InputForm width={11} label='Indice client' name='NUMCLI' type='label' notRequired />
                  <InputForm width={11} label='Nom client' name='lib_NUMCLI' type='label' notRequired />
                  <InputForm width={11} label='Pénalité' name='PENALITE' type='label' notRequired />
                  <InputForm width={11} label='Frais interruption' name='FRAISINTERRUP' type='label' notRequired />
                  <InputForm width={11} label='Date interruption' name='DATINTERUP' type='date' notRequired disabled={interActif} />
                  <InputForm width={11} label='Intérêts acquis' name='INTACQUIS' type='label' notRequired />
                  <InputForm width={11} label='Intérêts dus' name='INTDUS' type='label' notRequired />
                </Box>

                <Box fontStyle="inherit" className='border-left' style={{ flex: 1 }}>
                  <InputForm width={11} label='Type BDC' name='TYPBDC' type='label' notRequired />
                  <InputForm width={11} label='Type imputation' name='TYPIMPUTINT' type='label' notRequired />
                  <InputForm width={11} label='Fréquence' name='FREQPROV' type='label' notRequired />
                  <InputForm width={11} label='Durée' name='DUREE' type='label' notRequired />
                  <InputForm width={11} label='Taxe pénalité' name='TAXPENAL' type='label' notRequired />
                  <InputForm width={11} label='État' name='CDETAT' type='label' notRequired />
                  <InputForm width={11} label='Jours écoulés' name='JOURECOULE' type='label' notRequired />
                  <InputForm width={11} label='Mode sortie' name='MODEINTER' type='label' notRequired />
                  <InputForm width={11} label='Effet intérêts' name='INTEFFET' type='label' notRequired />
                </Box>

                <Box fontStyle="inherit" className='border-left' style={{ flex: 1 }}>
                  <InputForm width={11} label='Montant' name='MONTANT' type='label' notRequired />
                  <InputForm width={11} label='Date début' name='DATDEBUT' type='label' notRequired />
                  <InputForm width={11} label='Date fin' name='DATFIN' type='label' notRequired />
                  <InputForm width={11} label='Taux intérêt' name='TXINT' type='label' notRequired />
                  <InputForm width={11} label='Taux taxe' name='TXTAXE' type='label' notRequired />
                  <InputForm width={11} label='Jours restants' name='JOURRESTANT' type='number' notRequired disabled={interActif} />
                  <SearchModal notremove notRequired width={11} Taille1="35%" Taille2="65%" form={form} Titre="Recherche motif" label='Motif' name='MOTIF' Parametre={searchMotif} />
                  <InputForm width={11} label='Observation' name='OBSERVATION' type='text' notRequired />
                </Box>

                <Box fontStyle="inherit" className='border-left' style={{ flex: 1 }}>
                  <InputForm width={11} label='Compte dépôt' name='CPTDEPOT' type='label' notRequired />
                  <InputForm width={11} label='Disponible' name='DISPONCPTDEPOT' type='label' notRequired />
                  <InputForm width={11} label='Compte BDC' name='CPTBDC' type='label' notRequired />
                  <InputForm width={11} label='Disponible' name='DISPONCPTBDC' type='label' notRequired />
                  <InputForm width={11} label='Compte intérêts' name='CPTINTCLI' type='label' notRequired />
                  <InputForm width={11} label='Disponible' name='DISPONCPTINT' type='label' notRequired />
                  <InputForm width={11} label='Préavis (jours)' name='PREAVISINTERUPT' type='label' notRequired />
                  <SearchModal notremove notRequired disabled={enregActve} width={11} Taille1="35%" Taille2="65%" form={form} Titre='Recherche document' label='Document' name='DOCREQ' Parametre={searchDocReq}
                    ExecuteafterSelect1={() => setDocLibelle(String(form.getFieldValue('lib_DOCREQ') || '') || null)} />
                  <Upload beforeUpload={handleUploadDoc} showUploadList={false} accept=".pdf,.png,.jpg,.jpeg">
                    <Button size='small' icon={<UploadOutlined />} disabled={enregActve || !docLibelle} block style={{ marginTop: 4 }}>
                      Uploader le document
                    </Button>
                  </Upload>
                  {/* Tags de statut + fichiers uploadés — clic pour
                      prévisualiser */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                    {docRequis.map((d: any) => {
                      const fourni = pendingDocs.some((p) => p.IDREQ === d.IDREQ);
                      return (
                        <span key={d.IDREQ} style={{
                          fontSize: 11, padding: '2px 8px', borderRadius: 10,
                          border: `1px solid ${fourni ? '#116599' : '#d4380d'}`,
                          background: fourni ? '#e8f4fb' : '#fff1f0',
                          color: fourni ? '#116599' : '#d4380d',
                        }}>
                          {d.OBLIGATOIRE === 'O' ? '* ' : ''}{d.LIBDOC}{fourni ? ' ✓' : ' ✗'}
                        </span>
                      );
                    })}
                  </div>
                  {pendingDocs.map((d, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginTop: 4 }}>
                      <FilePdfOutlined style={{ color: '#b00' }} />
                      <span
                        style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer', color: '#116599', textDecoration: 'underline' }}
                        title={d.NOMFICHIER}
                        onClick={() => {
                          const win = window.open();
                          win?.document.write(`<iframe src="${d.PIECEJOINTE}" style="width:100%;height:100vh;border:0"></iframe>`);
                        }}
                      >
                        <b>{d.LIBDOC}</b> — {d.NOMFICHIER}
                      </span>
                      <DeleteOutlined style={{ color: 'darkred', cursor: 'pointer' }} onClick={() => setPendingDocs(pendingDocs.filter((_, j) => j !== i))} />
                    </div>
                  ))}
                </Box>

              </Box>

            </Box>
          </div>
        </div>
      ),
    }
  ];

  return (
    <div className='tab'>
      <Typography align='center' className='formTitle'> <b> INTERRUPTION BON DE CAISSE </b> </Typography>
      <Spin spinning={loading} size='large' style={{ color: '#116599' }}>
        <Form autoComplete="off" labelAlign='left' form={form} onFinish={onFinish} onFinishFailed={onFinishFailed} onValuesChange={onValuesChange} style={{ minWidth: 'auto', maxWidth: 'auto' }}>
          <div className='bG'>
            <legend><Typography color={"#116599"}>Actions</Typography></legend>
            <Form.Item shouldUpdate className='bouttong'>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button size='large' className='bt_add' onClick={() => { consult(); }} disabled={consulActive} type="ghost" htmlType="button" block  >
                  Consulter
                </Button>
                <Button size='large' className='bt_add' onClick={() => { calculer(selected || {}); calculInterruptionBDC(0); }} disabled={calcActive} type="ghost" htmlType="button" block  >
                  Calculer
                </Button>
                <ExportMenu disabled={impActive} onExport={handleExport} />
                <Button size='large' className='bt_add' disabled={enregActve} type="ghost" htmlType="submit" block>
                  Demander
                </Button>
                <Button size='large' onClick={handleAnulle} disabled={annulActive} className='bt_add' type="ghost" htmlType="reset" block>
                  Annuler
                </Button>
              </Space>
            </Form.Item>
          </div>
          <div className='clientP'>
            <div>
              <div >
                <Tabs size='small' type="card" defaultActiveKey="1" items={items} />
                {tabElement}
              </div>
            </div>
            <div>
            </div>
          </div>
        </Form>
      </Spin>

    </div>
  );
}
